import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { useModuleStore, MODULE_NAV, ERP_MODULES } from '../store/moduleStore';
import { usePermissionStore } from '../store/permissionStore';
import { useAuthStore } from '../store/authStore';
import { useCompanyPath, useCompanySlug, stripCompanyPrefix, companyPath } from '../utils/routing';
import { navPermission } from '../utils/navPermissions';

const Sidebar: React.FC<{ collapsed: boolean; mobileOpen?: boolean; onNavigate?: () => void }> = ({ collapsed, mobileOpen = false, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeModuleId, setModule } = useModuleStore();
  const companySlug = useCompanySlug();
  const modulesPath = useCompanyPath('/modules');
  const dashboardPath = useCompanyPath('/dashboard');
  const activeModule = ERP_MODULES.find(m => m.id === activeModuleId);
  const { can, loaded: permsLoaded } = usePermissionStore();
  const { user } = useAuthStore();
  const allNavItems = activeModuleId ? (MODULE_NAV[activeModuleId] || []) : [];
  const navItems = allNavItems.filter(item => {
    if (item.path === '/admin/platform' && !user?.is_superuser) return false;
    if (!permsLoaded) return true;
    const perm = navPermission(item.path);
    if (!perm) return true;
    return can(perm.module, perm.action);
  });

  const relPath = stripCompanyPrefix(location.pathname, companySlug);

  const isActive = (path: string) => {
    if (path.includes('?')) {
      const [p, q] = path.split('?');
      if (relPath !== p && !relPath.startsWith(p)) return false;
      const itemTab = new URLSearchParams(q).get('tab') || 'home';
      const curTab = new URLSearchParams(location.search).get('tab') || 'home';
      return itemTab === curTab;
    }
    return relPath === path || (path !== '/dashboard' && relPath.startsWith(path));
  };

  const goHome = () => { setModule(null); onNavigate?.(); navigate(modulesPath); };
  const toPath = (path: string) => companyPath(companySlug, path);

  const navClass = (active: boolean) =>
    `group relative flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-medium transition-all mb-0.5 ${
      active ? 'nav-item-active' : 'nav-item'
    } ${collapsed ? 'justify-center px-2' : ''}`;

  return (
    <aside className={`fixed left-0 top-0 h-screen bg-surface-sidebar border-r border-border text-content z-40 flex flex-col transition-transform duration-300 shadow-sm w-64 ${collapsed ? 'md:w-16' : 'md:w-64'} ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
      {/* Header */}
      <div className="h-14 border-b border-border flex items-center flex-shrink-0 px-3 gap-2">
        {collapsed ? (
          <button onClick={goHome} className="w-8 h-8 gradient-bg rounded-xl flex items-center justify-center font-bold text-sm text-white shadow-sm hover:opacity-90 transition-opacity mx-auto" title="Home">
            S
          </button>
        ) : (
          <Logo size="sm" />
        )}
      </div>

      {/* Module badge + back button */}
      {!collapsed && activeModule && (
        <div className="px-3 py-2 border-b border-border flex-shrink-0">
          <button onClick={goHome}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-content-muted hover:text-content hover:bg-surface-muted text-xs transition-all group">
            <svg className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            <span>All Modules</span>
          </button>
          <div className="mt-1.5 px-2 py-1.5 rounded-lg flex items-center gap-2 bg-surface-muted">
            <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${activeModule.color} flex items-center justify-center text-xs flex-shrink-0`}>
              {activeModule.icon}
            </div>
            <span className="text-xs font-semibold text-content truncate">{activeModule.name}</span>
          </div>
        </div>
      )}

      {/* Dashboard link */}
      <div className="px-2 pt-2 flex-shrink-0">
        <Link to={dashboardPath} onClick={onNavigate} className={navClass(isActive('/dashboard'))} title={collapsed ? 'Dashboard' : undefined}>
          <span className="text-base leading-none flex-shrink-0">📊</span>
          {!collapsed && <span>Dashboard</span>}
          {collapsed && (
            <span className="pointer-events-none absolute left-full ml-2 px-2.5 py-1.5 bg-surface-card border border-border text-content text-xs rounded-lg whitespace-nowrap opacity-0 invisible group-hover:visible group-hover:opacity-100 transition-all">
              Dashboard
            </span>
          )}
        </Link>
      </div>

      {/* Module Nav Items */}
      <nav className="flex-1 overflow-y-auto py-1 px-2 space-y-0.5">
        {navItems.length === 0 && !activeModuleId && !collapsed && (
          <div className="mt-4 px-3 text-xs text-content-muted text-center py-4">
            ← Select a module from<br/>the home screen
          </div>
        )}

        {navItems.map(item => {
          const act = isActive(item.path);
          return (
            <Link key={item.path} to={toPath(item.path)} onClick={onNavigate} title={collapsed ? item.label : undefined} className={navClass(act)}>
              {act && !collapsed && <span className="absolute left-0 top-2 bottom-2 w-0.5 bg-primary-500 rounded-r-full" />}
              <span className="text-base leading-none flex-shrink-0">{item.icon}</span>
              {!collapsed && <span className="truncate leading-tight">{item.label}</span>}
              {collapsed && (
                <span className="pointer-events-none absolute left-full ml-2 px-2.5 py-1.5 bg-surface-card border border-border text-content text-xs rounded-lg whitespace-nowrap opacity-0 invisible group-hover:visible group-hover:opacity-100 transition-all">
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}

        {!collapsed && activeModuleId && (
          <div className="mt-4 pt-3 border-t border-border">
            <div className="text-[10px] font-bold uppercase tracking-widest text-content-muted px-2 mb-2">Quick Access</div>
            {[
              { path: '/approvals', icon: '✅', label: 'Approvals' },
              { path: '/reports', icon: '📈', label: 'Reports' },
            ].map(item => (
              <Link key={item.path} to={toPath(item.path)} onClick={() => { setModule('reports'); onNavigate?.(); }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-content-muted hover:text-content hover:bg-surface-muted transition-all mb-0.5">
                <span>{item.icon}</span><span>{item.label}</span>
              </Link>
            ))}
          </div>
        )}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="px-3 py-2 border-t border-border flex-shrink-0">
          <button onClick={goHome}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-surface-muted hover:bg-primary-50 transition-all text-left group">
            <div className="w-6 h-6 rounded-lg gradient-bg flex items-center justify-center text-xs font-bold text-white">S</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-content truncate">SMEPRO360</div>
              <div className="text-[10px] text-content-muted">← Back to modules</div>
            </div>
            <svg className="w-3 h-3 text-content-muted group-hover:text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18M3 12l4-4m-4 4l4 4" />
            </svg>
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
